import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (inlined — no external common schema needed)
// ────────────────────────────────────────────────────────────────────────────

const PaginationSchema = z.object({
  lastKey: z.string().optional().nullable(),
  limit: z.number().int().min(1).max(1000).default(200),
  nextToken: z.string().optional().nullable(),
});

const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    count: z.number().int().min(0),
    items: z.array(itemSchema),
    lastKey: z.string().optional().nullable(),
    nextToken: z.string().optional().nullable(),
  });

// ────────────────────────────────────────────────────────────────────────────
// Contract
// ────────────────────────────────────────────────────────────────────────────

export const ContractStatusSchema = z.enum(['OPEN', 'CLOSED']);

export type IContractStatus = z.infer<typeof ContractStatusSchema>;

export const ContractLineItemSchema = z.object({
  availableQuantity: z.number().nullable(),
  destination: z.string().nullable(),
  mode: z.string().nullable(),
  origin: z.string().nullable(),
  poNumber: z.string().nullable(),
  product: z.string(),
  shippedQuantity: z.number().nullable(),
  titleTransfer: z.string().nullable(),
  uom: z.string().nullable(),
});

export type IContractLineItem = z.infer<typeof ContractLineItemSchema>;

export const ContractSchema = z.object({
  accountName: z.string(),
  availableQuantity: z.number().nullable(),
  contractEndDate: z.string().nullable(),
  contractName: z.string(),
  contractStartDate: z.string().nullable(),
  currency: z.string().nullable(),
  customerContact: z.string().nullable(),
  id: z.string(),
  kochParty: z.string().nullable(),
  lineItems: z.array(ContractLineItemSchema),
  paymentTerms: z.string().nullable(),
  remainingQuantity: z.number().nullable(),
  remainingQuantityEffective: z.number().nullable(),
  shipmentWindowEnd: z.string().nullable(),
  shipmentWindowStart: z.string().nullable(),
  shippedQuantity: z.number().nullable(),
  shippingMode: z.string().nullable(),
  stage: z.string().nullable(),
  status: ContractStatusSchema,
  titleTransfer: z.string().nullable(),
  totalContractVolume: z.number().nullable(),
});

export type IContract = z.infer<typeof ContractSchema>;

export const ContractListQuerySchema = PaginationSchema.extend({
  accountName: z.string().optional(),
  contractName: z.string().optional(),
  kochParty: z.string().optional(),
  product: z.string().optional(),
  status: ContractStatusSchema.optional(),
});

export type IContractListQuery = z.infer<typeof ContractListQuerySchema>;

export const ContractListResponseSchema =
  PaginatedResponseSchema(ContractSchema);

export type IContractListResponse = z.infer<typeof ContractListResponseSchema>;
